import * as React from "react"
import { Input } from "./input"
import { cn } from "../../lib/utils"

const CurrencyInput = React.forwardRef(({ className, value, onChange, placeholder, ...props }, ref) => {
  const [inputValue, setInputValue] = React.useState('')
  const [isFocused, setIsFocused] = React.useState(false)
  const inputRef = React.useRef(null)

  // Format number with commas for display
  const formatCurrency = (num) => {
    if (!num || num === 0) return '$0.00'
    return `$${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  }

  React.useEffect(() => {
    if (!isFocused) {
      setInputValue(formatCurrency(value))
    }
  }, [value, isFocused])

  const handleFocus = () => {
    setIsFocused(true)
    setInputValue(value ? `$${value.toString()}` : '$')
    // Select all text when focused
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.select()
      }
    }, 0)
  }

  const handleBlur = () => {
    setIsFocused(false)
    // Extract numeric value from input (remove $ and parse)
    const numericValue = parseFloat(inputValue.replace(/^\$/, '')) || 0
    onChange?.(numericValue)
  }

  const handleChange = (e) => {
    let newValue = e.target.value

    // If focused, ensure $ symbol is present and handle number input
    if (isFocused) {
      // Remove $ symbol temporarily to process the number
      let numberPart = newValue.replace(/^\$/, '')

      // Allow only digits and decimal point
      numberPart = numberPart.replace(/[^\d.]/g, '')

      // Limit to 2 decimal places
      if (numberPart.includes('.')) {
        const parts = numberPart.split('.')
        if (parts[1] && parts[1].length > 2) {
          numberPart = `${parts[0]}.${parts[1].slice(0, 2)}`
        }
      }

      // Add $ back
      newValue = `$${numberPart}`
    }

    setInputValue(newValue)
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.target.blur()
    }
  }

  return (
    <Input
      ref={ref || inputRef}
      type="text"
      className={cn(className)}
      placeholder={placeholder}
      value={inputValue}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      {...props}
    />
  )
})

CurrencyInput.displayName = "CurrencyInput"

export { CurrencyInput }